import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import SendIcon from '@assets/images/comment-send-icon.svg?react'
import AddIcon from '@mui/icons-material/AddRounded'

import { SelectEditableFieldModel } from '@types'

import { Input } from '@components/Input'
import { Option, Select } from '@components/Select'

import { useToggle } from '@utils/hooks'

import { v4 } from 'uuid'
import {
  AddButtonContainer,
  AddOptionButton,
  AddOptionIcon,
  AddOptionLabel,
  Container,
  InputContainer,
  ItemContainer,
  SelectedItemLabel,
} from './SubmitterFieldSelectEditable.styled'

type SubmitterFieldSelectEditableProps = {
  field: SelectEditableFieldModel
  onChange: (updates: Partial<SelectEditableFieldModel>) => void
  onRedirect: (id: string) => void
  disabled?: boolean
}

export const SubmitterFieldSelectEditable: React.FC<SubmitterFieldSelectEditableProps> = ({
  field,
  disabled = false,
  onChange,
  onRedirect,
}: SubmitterFieldSelectEditableProps) => {
  const { t } = useTranslation()
  const [isAddingOption, toggle] = useToggle(false)
  const [value, setValue] = useState<string>('')

  const handleAddOption = () => {
    if (!value.trim()) return

    const myId = v4()

    onChange({
      value: myId,
      options: [
        ...field.options,
        {
          id: myId,
          label: value.trim(),
        },
      ],
    })

    toggle()
    setValue('')
  }

  const options: Option[] = field.options.map((option) => ({
    id: option.id,
    label: option.label,
    data: option,
  }))

  const getSelectedOption = (id?: string): Option | undefined => {
    const option = options.find((option) => option.id === id)
    return option
      ? {
          id: option.id,
          label: option.label,
          data: option,
          Component: () => (
            <ItemContainer>
              <SelectedItemLabel>{option.label}</SelectedItemLabel>
            </ItemContainer>
          ),
        }
      : undefined
  }

  return (
    <Container>
      <Select
        selected={getSelectedOption(field.value)}
        options={options}
        label={field.label}
        hideOptional
        placeholder={t(
          'modules_project-forms_components_FormSubmitter_components_SubmitterFieldSelectEditable_placeholder',
        )}
        onHide={() => {
          setValue('')
          isAddingOption && toggle()
        }}
        disableAutoScroll={isAddingOption}
        required={field.required}
        disabled={disabled}
        onClick={(value) => {
          value?.data.redirectTo && onRedirect(value.data.redirectTo)
          onChange({ value: value ? value?.id : undefined })
        }}
        FooterComponent={() => {
          if (isAddingOption)
            return (
              <InputContainer>
                <Input
                  autoFocus
                  placeholder={t(
                    // eslint-disable-next-line max-len
                    'modules_project-forms_components_FormSubmitter_components_SubmitterFieldSelectEditable_add_option_placeholder',
                  )}
                  type="text"
                  maxLength={255}
                  value={value}
                  onChange={setValue}
                  required
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      e.stopPropagation()

                      handleAddOption()
                    }
                  }}
                />

                <AddButtonContainer
                  disabled={!value}
                  onClick={() => {
                    if (!value) return
                    handleAddOption()
                  }}
                >
                  <SendIcon />
                </AddButtonContainer>
              </InputContainer>
            )

          return (
            <AddOptionButton onClick={toggle}>
              <AddOptionIcon>
                <AddIcon />
              </AddOptionIcon>
              <AddOptionLabel>
                {t('modules_project-forms_components_FormSubmitter_components_SubmitterFieldSelectEditable_add_option')}
              </AddOptionLabel>
            </AddOptionButton>
          )
        }}
      />
    </Container>
  )
}
